export interface Review {
  id: string;
  author: string;
  rating: number;
  text: string;
  date: string;
}

export interface PropertyPriceRange {
  min: number;
  max: number;
  currency?: string;
  unit?: string;
}

export interface CommuteTimes {
  hinjewadi?: number;
  magarpatta?: number;
  eon_it_park?: number;
  airport?: number;
  [key: string]: number | undefined;
}

export interface Neighborhood {
  id: string;
  name: string;
  microMarket: string;
  nriCommunityScore: number;
  description: string;
  pros: string[];
  cons: string[];
  commuteMinutes: CommuteTimes;
  propertyPriceRange: {
    rent3bhk: PropertyPriceRange;
    buy3bhk: PropertyPriceRange;
  };
  nearbySchools: string[];
  popularSocieties: string[];
  mentionedInChat: number;
  chatSentiment: string;
  chatQuote?: string;
  lat: number;
  lng: number;
}

export interface School {
  id: string;
  name: string;
  location: string;
  nearbyLocalities: string[];
  curriculum: string[];
  gradesOffered: string;
  academicYearStart: string;
  feeRangeAnnual: PropertyPriceRange;
  nriFriendly: boolean;
  midYearAdmission: boolean;
  hasPreschool?: boolean;
  mentionedInChat: number;
  chatSentiment: string;
  highlights: string[];
  concerns: string[];
  chatQuotes: string[];
  website: string | null;
  admissionTimeline: string;
  lat: number;
  lng: number;
}

export interface ChecklistTask {
  id: string;
  task: string;
  priority: 'high' | 'medium' | 'low';
  details: string;
  sourceCountry: string[];
  chatMentioned: boolean;
  chatQuote?: string;
}

export interface ChecklistCategory {
  name: string;
  tasks: ChecklistTask[];
}

export interface ChecklistPhase {
  id: string;
  name: string;
  timeframe: string;
  categories: ChecklistCategory[];
}

export interface ChecklistData {
  metadata: {
    version: string;
    lastUpdated: string;
    source: string;
  };
  phases: ChecklistPhase[];
}

// For user state persistence
export interface UserTaskState {
  id: string;
  completed: boolean;
  included: boolean; // For customization (add/remove from list)
}

export interface InvestmentTip {
  title: string;
  category: 'Tax' | 'RNOR' | '401k' | 'Real Estate';
  content: string;
}

export interface FAQ {
  question: string;
  answer: string;
  fromChat: boolean;
  chatQuote?: string;
}

export interface FAQCategory {
  id: string;
  name: string;
  faqs: FAQ[];
}

export interface FAQData {
  metadata: {
    version: string;
    lastUpdated: string;
    source: string;
  };
  categories: FAQCategory[];
}

export interface Article {
  id: string;
  title: string;
  category: string;
  categoryColor: 'indigo' | 'emerald' | 'amber' | 'rose';
  excerpt: string;
  readTimeMinutes: number;
  publishedDate: string;
  heroImage: string | null;
  heroVisual?: 'rnor' | 'school' | 'rental';
  appView: AppView | null;
  comingSoon: boolean;
}

export interface ChatMessage {
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
}

export interface UserProfile {
  sourceCountry: string;
  familyType: string;
  childrenAges: string[];
  workLocation: string;
  timeline: string;
  budget: string;
  createdAt?: string;
  isPersonalized?: boolean;
}

export const DEFAULT_USER_PROFILE: UserProfile = {
  sourceCountry: 'Other',
  familyType: 'family_with_kids',
  childrenAges: [],
  workLocation: 'unsure',
  timeline: 'exploring',
  budget: 'rent_50_80k',
};

export interface AppData {
  neighborhoods: Neighborhood[];
  schools: School[];
  checklist: ChecklistData;
  investments: InvestmentTip[];
  faqs: FAQData;
}

export enum AppView {
  LANDING = 'LANDING',
  QUIZ = 'QUIZ',
  DASHBOARD = 'DASHBOARD',
  MAP = 'MAP',
  NEIGHBORHOODS = 'NEIGHBORHOODS',
  SCHOOLS = 'SCHOOLS',
  CHECKLIST = 'CHECKLIST',
  INVESTMENTS = 'INVESTMENTS',
  COMMUNITY = 'COMMUNITY',
  GUIDES = 'GUIDES',
  NRI_GUIDE = 'NRI_GUIDE',
  RNOR_GUIDE = 'RNOR_GUIDE',
  SCHOOL_GUIDE = 'SCHOOL_GUIDE',
}